import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { Project, SceneWithShots, ProjectConfig, Shot, BackgroundMusicTrack } from '../types/database';

const PROJECTS_KEY = 'projects';

// Check if running in demo mode
const isDemoMode = !import.meta.env.VITE_SUPABASE_URL ||
                   import.meta.env.VITE_SUPABASE_URL === 'http://localhost:54321';

// Demo projects storage
const demoProjects: Project[] = [];

// Fetch all projects for the current user
export function useProjects() {
  return useQuery({
    queryKey: [PROJECTS_KEY],
    queryFn: async () => {
      if (isDemoMode) {
        // Return demo projects
        return demoProjects;
      }

      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Project[];
    },
  });
}

// Fetch a single project with all related data
export function useProject(projectId: string | null) {
  return useQuery({
    queryKey: [PROJECTS_KEY, projectId],
    queryFn: async () => {
      if (!projectId) return null;

      // Fetch project
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .single();

      if (projectError) throw projectError;

      // Fetch project config
      const { data: config, error: configError } = await supabase
        .from('project_configs')
        .select('*')
        .eq('project_id', projectId)
        .single();

      if (configError && configError.code !== 'PGRST116') throw configError;

      // Fetch scenes with shots
      const { data: scenes, error: scenesError } = await supabase
        .from('scenes')
        .select(`
          *,
          shots (*)
        `)
        .eq('project_id', projectId)
        .order('order_index');

      if (scenesError) throw scenesError;

      // Fetch background music tracks
      const { data: musicTracks, error: musicError } = await supabase
        .from('background_music_tracks')
        .select('*')
        .eq('project_id', projectId)
        .order('order_index');

      if (musicError) console.warn('Failed to fetch music tracks:', musicError);

      // Sort shots within each scene
      const scenesWithSortedShots = (scenes || []).map((scene: SceneWithShots) => ({
        ...scene,
        shots: (scene.shots || []).sort((a: Shot, b: Shot) => a.order_index - b.order_index),
      }));

      return {
        project: project as Project,
        config: config as ProjectConfig | null,
        scenes: scenesWithSortedShots as SceneWithShots[],
        musicTracks: (musicTracks || []) as BackgroundMusicTrack[],
      };
    },
    enabled: !!projectId,
  });
}

// Create a new project
export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      title,
      config,
    }: {
      title: string;
      config: Omit<ProjectConfig, 'id' | 'project_id' | 'created_at' | 'updated_at'>;
    }) => {
      if (isDemoMode) {
        // Create demo project
        const newProject: Project = {
          id: `demo-${Date.now()}`,
          title,
          user_id: 'demo-user',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          status: 'draft',
        };
        demoProjects.push(newProject);
        return newProject;
      }

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('User not authenticated');
      }

      // Create project with user_id
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .insert({ title, user_id: user.id })
        .select()
        .single();

      if (projectError) throw projectError;

      // Create project config
      const { error: configError } = await supabase
        .from('project_configs')
        .insert({
          project_id: project.id,
          ...config,
        });

      if (configError) throw configError;

      return project as Project;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROJECTS_KEY] });
    },
  });
}

// Update a project
export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      updates,
    }: {
      projectId: string;
      updates: Partial<Project>;
    }) => {
      const { data, error } = await supabase
        .from('projects')
        .update(updates)
        .eq('id', projectId)
        .select()
        .single();

      if (error) throw error;
      return data as Project;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [PROJECTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [PROJECTS_KEY, variables.projectId] });
    },
  });
}

// Delete a project
export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (projectId: string) => {
      if (isDemoMode) {
        const index = demoProjects.findIndex(p => p.id === projectId);
        if (index > -1) {
          demoProjects.splice(index, 1);
        }
        return;
      }

      const { error } = await supabase.from('projects').delete().eq('id', projectId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROJECTS_KEY] });
    },
  });
}
